import React from "react";
import { useTranslation } from "react-i18next";
import { RoomMovie } from "../../../api/room";
import { movieTitleHandler } from "../../../utils/helpers";
import styles from "./RoomMovieCard.module.scss";
import useLikeClick from "../../../hooks/useLikeClick";
import useFavouriteClick from "../../../hooks/useFavouriteClick";
import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import LikeButton from "../../../components/shared/LikeButton";
import useInterfaceContext from "../../../components/providers/interface";
import {setVoteCount} from "../../../store/slices/lists.slice";
import { useDispatch } from "react-redux";
import { genresData } from "../../../constants/structure";
import SavedIcon from "../../../components/icons/searchIcons/savedIcon";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

interface RoomMovieCardProps {
  movie: RoomMovie;
  onClick?: () => void;
  isActive?: boolean;
  isVoted?: boolean;
}

const getPosterPath = (lng: string, movie: RoomMovie): string => {
  if (lng === "ru" && movie.poster_path_ru) return movie.poster_path_ru;
  if (lng === "uk" && movie.poster_path_ua) return movie.poster_path_ua;
  if (lng === "ar" && movie.poster_path_ar) return movie.poster_path_ar;
  return movie.poster_path;
};

const RoomMovieCard: React.FC<RoomMovieCardProps> = ({ movie, onClick, isActive = false, isVoted = false }) => {
  const { i18n, t } = useTranslation();
  const title = movieTitleHandler(i18n.language, movie) || movie.title;
  const dirData = i18n.language === "ar" ? "rtl" : "ltr";
  const { isMobile } = useInterfaceContext();
  const { likesList, likeHandler } = useLikeClick();
  const { favoritesList, favouritesHandler } = useFavouriteClick();
  const poster = getPosterPath(i18n.language, movie);
  const year = movie.release_date?.slice(0, 4);
  const genreList = movie.genres
    ? JSON.parse(movie.genres)
      .slice(0, 2)
      .map((id: number) => genresData.find((genre) => genre.id === id)?.name)
      .filter(Boolean)
      .map((name: string) => t(name))
    : [];

  const dispatch = useDispatch();

  const likedIds = Array.isArray(likesList?.data?.data) ? likesList.data.data : [];
  const favoriteIds = Array.isArray(favoritesList?.data?.results) ? favoritesList.data.results : [];

  const imdbRating = movie ? (movie.imdb_rating?.toFixed(1) || movie.vote_average?.toFixed(1)) : null;
  const tomatoRating = movie ? (movie.rotten_tomatoes_rating?.toFixed(0)) : null;
  const likesCount = movie?.likes_count ?? 0;
  const liked = likedIds.includes(movie.id);
  const isFav = favoriteIds.includes(movie.id);

  const updateVoteCount = (id: any, key: any, type: any) => {
    dispatch(setVoteCount({ id, key, type }));
  };

  const formatLikesCount = (count: number) => {
    if (count < 1000) return `${count}`;

    const compactCount = Math.floor(count / 100) / 10;
    return `${Number.isInteger(compactCount) ? compactCount.toFixed(0) : compactCount.toFixed(1)}k`;
  };

  return (
    <div
      onClick={onClick}
      className={`${styles.card} ${
        isActive ? styles.active : ""
      }`}
    >
      <div className={styles.posterWrap}>
        {poster ? (
          <img src={`${IMG_BASE}${poster}`} alt={title} className={styles.poster} />
        ) : (
          <div className={styles.posterPlaceholder}>{title}</div>
        )}
        {isVoted && (
          <div className={styles.votedOverlay}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
      </div>
      <div className={styles.info}>
        <div className={styles.cardHeading}>
          <div className={styles.title}> {title}</div>
          {!isMobile && (
          <div
            dir={dirData}
            className={styles.savedIconWrapper}
            onClick={(e: any) => {
              e.stopPropagation();
              favouritesHandler(e, movie, isFav);
            }}
          >
            <SavedIcon className={`${isMobile ? styles.savedIcon : styles.savedIconDesctop}`} inFavourites={isFav} />
          </div>
            )}
        </div>
        <div className={styles.meta}>
          <div className={styles.metaHolder}>
            {year && <span className={styles.year}>{year}</span>}
          </div>

          {genreList.length > 0 && (
            <div className={styles.genres}>
              {genreList.join(", ")}
            </div>
          )}
        </div>

        <div className={styles.cardActionWrapper}>
          {imdbRating && (
            <div className={styles.ratingItem}>
              <OverlayRatingIcon />
              <p className={styles.rating}>{imdbRating}</p>
            </div>
          )}
          {tomatoRating && (
            <div className={styles.ratingItem}>
              <OverlayTomatoRatingIcon />
              <p className={styles.rating}>{tomatoRating}</p>
            </div>
          )}
          {!isMobile && (
            <LikeButton
              clickHandler={likeHandler}
              direction="row"
              isMobile={isMobile}
              count={formatLikesCount(likesCount)}
              updateFieldLocal={updateVoteCount}
              liked={liked}
              item={movie}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomMovieCard;
